import React, { useEffect, useState } from "react";
import { Save, Loader2, Building2, Phone, Mail, MapPin, Globe, Facebook, Instagram, Linkedin, TrendingUp, Clock } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../hooks/use-toast";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";

const FIELDS = [
  { key: "org_name", label: "Organisation Name", icon: Building2 },
  { key: "phone", label: "Telephone No.", icon: Phone },
  { key: "email", label: "Email Address", icon: Mail },
  { key: "website", label: "Website URL", icon: Globe },
  { key: "address", label: "Address", icon: MapPin, area: true },
  { key: "facebook", label: "Facebook URL", icon: Facebook },
  { key: "instagram", label: "Instagram URL", icon: Instagram },
  { key: "linkedin", label: "LinkedIn URL", icon: Linkedin },
];

export default function Settings() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const readOnly = user?.kind !== "super_admin";

  useEffect(() => { api.get("/settings").then((r) => setForm(r.data)).catch(() => {}); }, []);

  const save = async () => {
    setSaving(true);
    try { await api.put("/settings", form); toast({ title: "Settings saved" }); }
    catch (e) { toast({ title: "Save failed", description: e?.response?.data?.detail || "", variant: "destructive" }); }
    finally { setSaving(false); }
  };

  if (!form) return <div className="text-slate-400">Loading…</div>;

  return (
    <div className="max-w-2xl">
      <h1 className="font-display text-2xl md:text-3xl font-bold text-[#012a1e]">Site Settings</h1>
      <p className="text-slate-500 mt-1 text-sm">Organisation contact details shown across the portal.{readOnly ? " (read-only)" : ""}</p>

      <div className="bg-white rounded-2xl border border-slate-100 p-6 mt-5 space-y-4">
        {FIELDS.map((f) => (
          <div key={f.key} className="space-y-2">
            <Label className="flex items-center gap-2"><f.icon size={15} className="text-[#004030]" /> {f.label}</Label>
            {f.area ? (
              <Textarea disabled={readOnly} value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className="min-h-20" />
            ) : (
              <Input disabled={readOnly} value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className="h-11" />
            )}
          </div>
        ))}
        {!readOnly && (
          <Button onClick={save} disabled={saving} className="bg-[#004030] hover:bg-[#013d2b] text-white rounded-full px-6 h-11">
            {saving ? <Loader2 className="animate-spin" size={16} /> : <><Save size={16} className="mr-1.5" /> Save Settings</>}
          </Button>
        )}
      </div>

      <h2 className="font-display text-xl md:text-2xl font-bold text-[#012a1e] mt-8">Marketing Spend</h2>
      <p className="text-slate-500 mt-1 text-sm">Total marketing spend for the current period. Powers cost-per-lead, cost-per-qualified & cost-per-deposit on the Analytics dashboard.</p>
      <div className="bg-white rounded-2xl border border-slate-100 p-6 mt-5 space-y-4" data-testid="marketing-spend-card">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="space-y-2 sm:col-span-2">
            <Label className="flex items-center gap-2"><TrendingUp size={15} className="text-[#004030]" /> Total Spend</Label>
            <Input data-testid="marketing-spend-input" type="number" min="0" step="0.01" disabled={readOnly}
              value={form.marketing_spend ?? 0}
              onChange={(e) => setForm({ ...form, marketing_spend: e.target.value === "" ? 0 : parseFloat(e.target.value) })}
              className="h-11" placeholder="0.00" />
          </div>
          <div className="space-y-2">
            <Label className="flex items-center gap-2"><Globe size={15} className="text-[#004030]" /> Currency</Label>
            <select data-testid="spend-currency-select" disabled={readOnly}
              value={form.spend_currency || "USD"}
              onChange={(e) => setForm({ ...form, spend_currency: e.target.value })}
              className="h-11 w-full rounded-md border border-slate-200 bg-white px-3 text-sm disabled:opacity-60">
              {["USD", "EUR", "GBP", "MYR", "INR"].map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
        {!readOnly && (
          <Button data-testid="save-marketing-spend-btn" onClick={save} disabled={saving} className="bg-[#004030] hover:bg-[#013d2b] text-white rounded-full px-6 h-11">
            {saving ? <Loader2 className="animate-spin" size={16} /> : <><Save size={16} className="mr-1.5" /> Save Spend</>}
          </Button>
        )}
      </div>

      <h2 className="font-display text-xl md:text-2xl font-bold text-[#012a1e] mt-8">Lead Response SLA</h2>
      <p className="text-slate-500 mt-1 text-sm">The first-response time limit after a new lead is auto-assigned. If the counsellor doesn't respond within this window, the lead is flagged as SLA breached and escalated to managers.</p>
      <div className="bg-white rounded-2xl border border-slate-100 p-6 mt-5 space-y-4" data-testid="sla-settings-card">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="space-y-2 sm:col-span-2">
            <Label className="flex items-center gap-2"><Clock size={15} className="text-[#004030]" /> First-response window (minutes)</Label>
            <Input data-testid="sla-minutes-input" type="number" min="1" step="1" disabled={readOnly}
              value={form.sla_first_response_minutes ?? 10}
              onChange={(e) => setForm({ ...form, sla_first_response_minutes: e.target.value === "" ? 10 : parseInt(e.target.value, 10) })}
              className="h-11" placeholder="10" />
          </div>
        </div>
        {!readOnly && (
          <Button data-testid="save-sla-btn" onClick={save} disabled={saving} className="bg-[#004030] hover:bg-[#013d2b] text-white rounded-full px-6 h-11">
            {saving ? <Loader2 className="animate-spin" size={16} /> : <><Save size={16} className="mr-1.5" /> Save SLA</>}
          </Button>
        )}
      </div>
    </div>
  );
}
